import React, { useState, useEffect, FC, useRef } from 'react';
import { Table, notification, Grid } from 'antd';
import type { TableProps } from 'antd';
import ClientModal from '../components/ClientModal';
import { Icons } from '@assets';
import FilterModal from './ui/FilterModal/FilterModal';
import PushNotifyModal from '../components/content/PushNotifyModal';
import { IPaginatedProps, TableParams } from '@types';
import {
    limitCardChanges,
    limitCardTrans,
    limitClients,
    optionsSelect,
} from '@utils/const';
import { useDispatch, useSelector } from 'react-redux';
import { authInfoSelect } from '@entities/auth';
import { AppDispatch } from '@config/store';
import { IClientsQuery } from '@entities/client/model/types';
import { getClients, getTopStats } from '@api/thunks/clients';
import { AnyObject } from 'antd/es/_util/type';
import { clientSelect, setBloket, setRemove } from '@entities/client';
import { ITemplate } from '@entities/card/types';
import { useDebounce } from '@hooks/useDebounce';
import { EClientsAction } from '@utils';
import HeaderInfo from '../components/HeaderInfo';
import { cssOverride } from '@utils/const';
import { PuffLoader } from 'react-spinners';
import { getClientColumns } from './ui/clientsColumns/clientsColumns';
import {
    blockClientRequest,
    deleteClientRequest,
    unBlockClientRequest,
} from '@api/services/clientsApi';
import {
    checkIsResponseStatus,
    openNotificationWithIcon,
} from '@utils/functions';
import { getCardTemplates } from '@api/thunks/card';
import { cardSelect } from '@entities/card';
import FilterInfo from '../components/FilterInfo';
import * as S from './styles';
import { colors } from '@styles';
import { useClientActions } from '@hooks/useClientActions';
import { useClientData } from '@hooks/useClientData';

const ClientsPage: FC = () => {
    const { shop } = useSelector(authInfoSelect);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const {
        selectValue: selectTopStats,
        total,
        clients,
        loading,
    } = useSelector(clientSelect);
    const { transactionCount, changesCount, templates } =
        useSelector(cardSelect);
    const [openedSettingsPopup, setOpenedSettingsPopup] =
        useState<boolean>(false);
    const [openedFilterPopup, setOpenedFilterPopup] = useState<boolean>(false);
    const [selectedClientData, setSelectedClientData] = useState<{
        client_id: number[];
        template_id: number[];
    }>({ client_id: [], template_id: [] });
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [selectValue, setSelectValue] = useState<number>(1);
    const [api, contextHolder] = notification.useNotification();

    const [transPagination, setTransPagination] = useState<IPaginatedProps>({
        page: 1,
        limit: limitCardTrans,
    });
    const [changesPagination, setChangesPagination] = useState<IPaginatedProps>(
        {
            page: 1,
            limit: limitCardChanges,
        }
    );

    const [selectAction, setSelectAction] = useState<EClientsAction>(
        EClientsAction.ACTIVE
    );
    const [show, setShow] = useState<boolean>(false);
    const [tableParams, setTableParams] = useState<TableParams>({
        pagination: { current: 1, pageSize: limitClients },
        field: null,
        order: null,
        filters: { isActive: null },
    });

    const dispatch = useDispatch<AppDispatch>();
    const isPushNotify = selectAction === EClientsAction.SEND_NOTIFICATION;
    const debouncedSearch = useDebounce(searchText);

    useEffect(() => {
        if (shop) {
            const { current, pageSize } = tableParams.pagination!;
            const request: IClientsQuery = {
                limit: pageSize!,
                offset: (current! - 1) * pageSize!,
            };

            if (debouncedSearch) {
                request.anyOf = debouncedSearch;
            }

            if (tableParams?.field && tableParams?.order) {
                request.order = `${
                    tableParams.field === 'fio' ? 'lastname' : tableParams.field
                },${tableParams.order === 'ascend' ? 'asc' : 'desc'}`;
            }

            let statusFilter = tableParams?.filters;

            if (
                Array.isArray(statusFilter.isActive) &&
                statusFilter.isActive.length === 1
            ) {
                request.isActive = statusFilter.isActive[0] as React.Key;
            }
            if (
                Array.isArray(statusFilter.sex) &&
                statusFilter.sex.length === 1
            ) {
                request.sex = statusFilter.sex[0] as React.Key;
            }
            if (
                Array.isArray(statusFilter.templates) &&
                statusFilter.templates.length > 0
            )
                request.template = statusFilter.templates.join(',') as string;
            if (Array.isArray(statusFilter.age))
                request.ageRange = statusFilter.age.join(',') as string;
            if (statusFilter.fromCreateDate)
                request.fromC = statusFilter.fromCreateDate.toString();
            if (statusFilter.toCreateDate)
                request.toC = statusFilter.toCreateDate.toString();
            if (Array.isArray(statusFilter.visit))
                request.visit = statusFilter.visit.toString();

            setTimeout(() => inputRef.current?.focus(), 0);
            dispatch(getClients({ shop_id: shop.id, params: request }));
        }
    }, [dispatch, tableParams, shop, debouncedSearch]);

    const clientIds = selectedRowKeys.map(key => {
        if (typeof key === 'string' && key.startsWith('client-')) {
            return parseInt(key.replace('client-', ''), 10);
        }
        return Number(key);
    });

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: AnyObject[]) => {
            const templatesIds = selectedRows.flatMap(row =>
                row?.templates?.map((template: ITemplate) => template.id)
            );

            setSelectedRowKeys(selectedRowKeys);
            setSelectedClientData({
                template_id: templatesIds,
                client_id: clientIds,
            });
        },
    };

    const handleTableChange: TableProps['onChange'] = (
        pagination,
        filters,
        sorter
    ) => {
        const sortResult = Array.isArray(sorter) ? sorter[0] : sorter;

        setTableParams(prevState => ({
            pagination,
            filters: { ...prevState.filters, ...filters },
            field: sortResult?.order ? (sortResult.field as string) : null,
            order: sortResult?.order ?? null,
        }));
    };

    const { handleShowClientModal, handlerClickId } = useClientActions({
        transPagination,
        changesPagination,
        setOpenedSettingsPopup,
    });

    useClientData(transPagination, changesPagination);

    const handleOpenFilterModal = () => {
        setOpenedFilterPopup(true);
    };

    const handleCloseFilterModal = () => {
        setOpenedFilterPopup(false);
    };

    const handleCloseNotifyModal = () => {
        setShow(false);
        setSelectAction(EClientsAction.BLOCK);
        setSelectValue(1);
    };

    const handleOpenNotifyModal = () => {
        setSelectAction(EClientsAction.SEND_NOTIFICATION);
        setShow(true);
    };

    useEffect(() => {
        if (shop) {
            dispatch(
                getTopStats({ period: selectTopStats.value, shop_id: shop.id })
            );
            dispatch(getCardTemplates(shop.id));
        }
    }, [selectTopStats, dispatch, shop]);

    const onChangeSelect = (value: number) => {
        setSelectValue(value);
        setSelectAction(optionsSelect[value - 1].label);
        setShow(false);
    };

    const loadMore = () => {
        if (transactionCount > transPagination.page * transPagination.limit) {
            const newPage = transPagination.page + 1;
            setTransPagination(prev => ({
                ...prev,
                page: newPage,
            }));
        }
    };

    const loadMoreCardChanges = () => {
        if (changesCount > changesPagination.page * changesPagination.limit) {
            const newPage = changesPagination.page + 1;

            setChangesPagination(prev => ({
                ...prev,
                page: newPage,
            }));
        }
    };

    const onActionsToggler = () => {
        if (selectedRowKeys && selectedRowKeys.length > 0 && shop) {
            clientIds.forEach(id => {
                if (selectValue === 1) {
                    blockClientRequest(id, shop.id)
                        .then(res => {
                            if (checkIsResponseStatus(res.status)) {
                                dispatch(
                                    setBloket({
                                        id: Number(id),
                                        isActive: false,
                                    })
                                );
                                openNotificationWithIcon(
                                    api,
                                    'info',
                                    'Ок',
                                    `Клиент ${id} заблокирован`
                                );
                            }
                        })
                        .catch(err =>
                            openNotificationWithIcon(
                                api,
                                'error',
                                'Error',
                                err.response.data.message
                            )
                        );
                } else if (selectValue === 2) {
                    unBlockClientRequest(id, shop.id)
                        .then(res => {
                            if (checkIsResponseStatus(res.status)) {
                                dispatch(
                                    setBloket({
                                        id: Number(id),
                                        isActive: true,
                                    })
                                );
                                openNotificationWithIcon(
                                    api,
                                    'success',
                                    'Ок',
                                    `Клиент ${id} активирован`
                                );
                            }
                        })
                        .catch(err =>
                            openNotificationWithIcon(
                                api,
                                'error',
                                'Error',
                                err.response.data.message
                            )
                        );
                } else if (selectValue === 6) {
                    deleteClientRequest(id, shop.id)
                        .then(res => {
                            if (checkIsResponseStatus(res.status)) {
                                dispatch(setRemove(Number(id)));
                                openNotificationWithIcon(
                                    api,
                                    'error',
                                    'Ок',
                                    `Клиент ${id} удален`
                                );
                            }
                        })
                        .catch(err =>
                            openNotificationWithIcon(
                                api,
                                'error',
                                'Error',
                                err.response.data.message
                            )
                        );
                } else if (selectValue === 3) {
                    setShow(true);
                }
            });
        }
    };

    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();

    const renderTableFooter = () => {
        return (
            <S.FooterWrap>
                <S.ActionWrap>
                    <S.StyledSelect
                        options={optionsSelect}
                        value={selectValue}
                        onChange={val => onChangeSelect(val as number)}
                    />
                    <S.StyledActionButton onClick={() => onActionsToggler()}>
                        <span>Применить</span>
                    </S.StyledActionButton>
                    <S.StyledCheckbox>
                        <span>для всех</span>
                    </S.StyledCheckbox>
                </S.ActionWrap>
            </S.FooterWrap>
        );
    };

    const columns = getClientColumns(handlerClickId, screens, templates);

    if (
        loading === 'loading' &&
        !tableParams.order &&
        !Array.isArray(tableParams.filters.isActive) &&
        !Array.isArray(tableParams.filters.sex) &&
        !Array.isArray(tableParams.filters.templates) &&
        !openedSettingsPopup
    ) {
        return <PuffLoader color={colors.loader} cssOverride={cssOverride} />;
    }

    return (
        <S.Wrapper>
            {contextHolder}
            <S.Header>
                <S.HeaderTitle>Клиенты</S.HeaderTitle>
            </S.Header>
            <HeaderInfo />
            <S.SearchWrap>
                <S.SearchInput
                    key="searchInputeField"
                    ref={inputRef}
                    placeholder="Поиск..."
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    autoFocus={!!searchText}
                />

                <S.ButtonBlock>
                    <S.StyledButton onClick={handleOpenFilterModal}>
                        <S.IconWrapper>
                            <img
                                src={Icons.IconSearchVect2}
                                alt="icon search2"
                            />
                        </S.IconWrapper>
                    </S.StyledButton>
                    <S.StyledButton onClick={handleOpenNotifyModal}>
                        <img src={Icons.IconSearchVect4} alt="icon search" />
                    </S.StyledButton>
                </S.ButtonBlock>
            </S.SearchWrap>
            <FilterInfo
                total={total}
                tableParams={tableParams}
                setTableParams={setTableParams}
            />
            <S.TableWrapper>
                <Table
                    size="small"
                    scroll={{ x: 1200 }}
                    rowSelection={rowSelection}
                    columns={columns}
                    rowKey={rec => `client-${rec.id}`}
                    dataSource={clients}
                    onChange={handleTableChange}
                    pagination={{
                        current: tableParams.pagination?.current,
                        pageSize: tableParams.pagination?.pageSize,
                        total,
                    }}
                    footer={renderTableFooter}
                />
            </S.TableWrapper>
            <S.Overlay
                display={
                    openedSettingsPopup ||
                    (show && isPushNotify) ||
                    openedFilterPopup
                        ? 'block'
                        : 'none'
                }
            />
            {openedSettingsPopup && (
                <ClientModal
                    loadMoreCardChanges={loadMoreCardChanges}
                    loadMore={loadMore}
                    setOpeningSettingsPopup={handleShowClientModal}
                />
            )}
            {openedFilterPopup && (
                <FilterModal
                    tableParams={tableParams}
                    setTableParams={setTableParams}
                    modalVisibilityToggler={() => handleCloseFilterModal()}
                />
            )}
            {isPushNotify && show && (
                <PushNotifyModal
                    selectedClientData={selectedClientData}
                    modalVisibilityToggler={() => handleCloseNotifyModal()}
                />
            )}
        </S.Wrapper>
    );
};

export default ClientsPage;
